import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaLienReleasePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Lien Release</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Vehicle Lien Release Process</h1>
        <p className="text-obsidian-300">How to remove a lienholder from your Nevada vehicle title after paying off your loan.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">What Is a Lien Release?</h2>
          <p>When you finance a vehicle, the lender (lienholder) is listed on your title as a secured party. Once you pay off the loan, the lienholder must release their interest in the vehicle. This is called a lien release, and it&apos;s required before you can sell or transfer the vehicle free and clear.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">How Lienholders Release a Lien in Nevada</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'Sign the Title', desc: 'The lienholder signs the "Lien Release" section on the back of the Nevada title.' },
              { title: 'Separate Release Form', desc: 'The lienholder provides a separate notarized lien release document if they don\'t have the physical title.' },
              { title: 'Electronic Release', desc: 'Some lenders submit electronic lien releases directly to the Nevada DMV.' },
              { title: 'Letter of Release', desc: 'A signed letter on lender letterhead confirming the lien is satisfied, along with the title.' },
            ].map(item => (
              <div key={item.title} className="border border-obsidian-100 rounded-lg p-4">
                <p className="font-semibold text-obsidian-900 text-sm mb-1">{item.title}</p>
                <p className="text-obsidian-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Steps to Get Your Clear Title</h2>
          <ol className="space-y-4">
            {[
              { step: '1', title: 'Pay Off Your Loan', desc: 'Make your final payment and request a payoff confirmation from your lender.' },
              { step: '2', title: 'Obtain Lien Release', desc: 'Contact your lender to obtain a signed lien release or have them sign the title.' },
              { step: '3', title: 'Submit to DMV', desc: 'Submit the title with lien release (or separate release document) to the Nevada DMV.' },
              { step: '4', title: 'Receive Clear Title', desc: 'The DMV will issue a new title in your name only, without the lienholder listed.' },
            ].map(item => (
              <li key={item.step} className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-obsidian-900 text-gold-400 rounded-full font-bold text-sm flex-shrink-0">{item.step}</div>
                <div><p className="font-semibold text-obsidian-900 text-sm">{item.title}</p><p className="text-obsidian-500 text-xs mt-0.5">{item.desc}</p></div>
              </li>
            ))}
          </ol>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need Help With a Lien Release?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services can help you process your lien release and obtain a clear title.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaLienReleasePage;
